import { Button } from "@material-ui/core";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import {
  ChangeOrderCSTab,
  ContentHeaderTab
} from "@saleor/changeOrderDomestic/views/ViewEditOrder/types";
import MyTypography from "@saleor/components/MyTypography";
import DialogCreateOrderError from "@saleor/createOrder/components/DialogCreateOrderError";
import useSales from "@saleor/createOrderDomestic/views/CreateOrderView/useSales";
import {
  ContractChangeOrderDomesticQuery,
  useGetCreditLimitQuery,
  useGetCustomerGroupMasterQuery
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import classNames from "classnames";
import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import AdditionalTab from "./Tab/AdditionalTab";
import PartnerTab from "./Tab/PartnerTab";
import SalesTab from "./Tab/SalesTab";

export interface HeaderTabProps {
  orderId?: string;
  dataOrder: ContractChangeOrderDomesticQuery["changeOrder"];
  applyAllKey?: number;
  onApplyAll?: (value) => void;
  submitKey?: number;
  onSubmitForm?: (value) => void;
  setActiveTabTotal?: (tab: ChangeOrderCSTab) => void;
  activeTab?: ContentHeaderTab;
  setActiveTab?: (value: ContentHeaderTab) => void;
  setOpenError?: (value: boolean) => void;
  setApplyKey?: (value: number) => void;
  setSubmitKey?: (value: number) => void;
  setBillToInsteaded: (value: string) => void;
  setShipToInsteaded: (value: string) => void;
  cancelEditCall?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      marginTop: "4rem"
    },
    tabB: {
      marginLeft: "-3rem"
    },
    active: {
      backgroundColor: theme.palette.info.main,
      "& .MuiTypography-root": {
        color: "#FFFFFF"
      }
    },
    notActive: {
      backgroundColor: theme.palette.grey[300],
      "& .MuiTypography-root": {
        color: theme.palette.grey[600]
      }
    },
    z0: {
      zIndex: 0
    },
    z1: {
      zIndex: 1
    },
    z2: {
      zIndex: 2
    },
    tabBox: {
      display: "flex"
    },
    boxContentTab: {
      background: "#FFF",
      border: `solid 1px ${theme.palette.info.dark}`,
      padding: "3.7rem 2.4rem"
    },
    innerTabActive: {
      WebkitClipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      width: "199px",
      height: "39px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "5px",
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      top: 1,
      left: 1,
      background: theme.palette.info.main,
      position: "absolute",
      borderBottom: 0,
      color: "#FFFFFF"
    },
    tableTab: {
      WebkitClipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      width: "200px",
      height: "40px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "6px",
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      position: "relative",
      justifyContent: "start",
      borderBottomLeftRadius: 0,
      borderBottom: 0
    },
    innerTab: {
      WebkitClipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      width: "199px",
      height: "39px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "5px",
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      top: 1,
      left: 1,
      background: theme.palette.grey[300],
      color: theme.palette.grey[600],
      position: "absolute",
      borderBottom: 0
    },
    tableTabActive: {
      background: theme.palette.info.dark
    },
    tableTabInactive: {
      background: theme.palette.grey[400]
    },
    zMax: {
      zIndex: 100
    },
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    }
  }),
  { name: "HeaderTabCS" }
);

const tabList = [
  {
    id: "partner-tab",
    code: ContentHeaderTab.partner,
    title: "Partner",
    index: "z3",
    first: true
  },
  {
    id: "additional-tab",
    code: ContentHeaderTab.additionalData,
    title: "Additional Data",
    index: "z2",
    first: false
  },
  {
    id: "sales-tab",
    code: ContentHeaderTab.sales,
    title: "Sales",
    index: "z1",
    first: false
  }
];

const HeaderTab: React.FC<HeaderTabProps> = props => {
  const {
    dataOrder,
    orderId,
    applyAllKey,
    onApplyAll,
    submitKey,
    onSubmitForm,
    setActiveTabTotal,
    setActiveTab,
    activeTab,
    setOpenError,
    setApplyKey,
    setSubmitKey,
    setBillToInsteaded,
    setShipToInsteaded,
    cancelEditCall
  } = props;

  const classes = useStyles();
  const intl = useIntl();
  const textMissingField = intl.formatMessage({
    defaultMessage: "โปรดระบุข้อมูล"
  });
  const headerTabSchema = Yup.object().shape({
    poNo: Yup.string()
      .required(textMissingField)
      .typeError(textMissingField),
    // deliveryBlock: Yup.string()
    //   .required(textMissingField)
    //   .typeError(textMissingField),
    incoterm: Yup.string()
      .required(textMissingField)
      .typeError(textMissingField),
    saleOrganization: Yup.string().required(),
    distributionChannel: Yup.string().required(),
    division: Yup.string().required(),
    saleGroup: Yup.string().required(),
    saleOffice: Yup.string().required()
  });

  const getSoldToCode = () => {
    let _soldToCode = "";
    // eslint-disable-next-line chai-friendly/no-unused-expressions
    dataOrder?.orderPartners?.forEach(orderPartner => {
      if (orderPartner?.partnerRole === "AG") {
        _soldToCode = orderPartner?.partnerNo;
      }
    });
    return _soldToCode;
  };

  const { refetch: callCreditLimit } = useGetCreditLimitQuery({
    variables: {
      input: {
        contractNo: dataOrder?.contractNo,
        salesOrgCode: dataOrder?.salesOrg,
        soldToCode: getSoldToCode()
      }
    },
    skip: true
  });

  const getInternalCommentsToWarehouse = () => {
    const _internalCommentsToWarehouse = dataOrder?.orderText?.find(
      item => item.itemNo === "000000" && item.textId === "Z001"
    );
    let _text = "";
    // eslint-disable-next-line chai-friendly/no-unused-expressions
    _internalCommentsToWarehouse?.textLine?.forEach(text =>
      _text === "" ? (_text += text.text) : (_text += `\n${text.text}`)
    );
    return _text;
  };

  const getInternalCommentsToLogistic = () => {
    const _internalCommentsToLogistic = dataOrder?.orderText?.find(
      item => item.itemNo === "000000" && item.textId === "Z002"
    );
    let _text = "";
    // eslint-disable-next-line chai-friendly/no-unused-expressions
    _internalCommentsToLogistic?.textLine?.forEach(text =>
      _text === "" ? (_text += text.text) : (_text += `\n${text.text}`)
    );
    return _text;
  };

  const getExternalCommentsToCustomer = () => {
    const _internalExternalCommentsToCustomer = dataOrder?.orderText?.find(
      item => item?.itemNo === "000000" && item?.textId === "Z067"
    );
    let _text = "";
    // eslint-disable-next-line chai-friendly/no-unused-expressions
    _internalExternalCommentsToCustomer?.textLine?.forEach(text =>
      _text === "" ? (_text += text.text) : (_text += `\n${text.text}`)
    );
    return _text;
  };

  const getProductInformation = () => {
    const _productInformation = dataOrder?.orderText?.find(
      item => item?.itemNo === "000000" && item?.textId === "ZK08"
    );
    let _text = "";
    // eslint-disable-next-line chai-friendly/no-unused-expressions
    _productInformation?.textLine?.forEach(text =>
      _text === "" ? (_text += text.text) : (_text += `\n${text.text}`)
    );
    return _text;
  };

  const getShipToPartyName = () => {
    const _shipToPartyName = dataOrder?.orderPartners?.find(
      item => item?.partnerRole === "WE" && item?.itemNo === "000000"
    );
    return `${_shipToPartyName?.partnerNo} - ${_shipToPartyName?.address[0]?.name}\n${_shipToPartyName?.address[0]?.street}, ${_shipToPartyName?.address[0]?.district}, ${_shipToPartyName?.address[0]?.city} ${_shipToPartyName?.address[0]?.postCode}`;
  };

  const getShipToPartyAddress = () => {
    const _shipToPartyAddress = dataOrder?.orderPartners?.find(
      item => item?.partnerRole === "WE" && item?.itemNo === "000000"
    );
    return `${_shipToPartyAddress?.address[0]?.addrNo} ${_shipToPartyAddress?.address[0]?.street} ${_shipToPartyAddress?.address[0]?.district} ${_shipToPartyAddress?.address[0]?.city} ${_shipToPartyAddress?.address[0]?.postCode}`;
  };

  const getBillToPartyName = () => {
    const _billToPartyName = dataOrder?.orderPartners?.find(
      item => item.partnerRole === "RE"
    );
    return `${_billToPartyName?.partnerNo} - ${_billToPartyName?.address[0]?.name}\n${_billToPartyName?.address[0]?.street}, ${_billToPartyName?.address[0]?.district}, ${_billToPartyName?.address[0]?.city} ${_billToPartyName?.address[0]?.postCode}`;
  };

  const getBillToPartyAddress = () => {
    const _billToPartyName = dataOrder?.orderPartners?.find(
      item => item.partnerRole === "RE"
    );
    return `${_billToPartyName?.address[0]?.addrNo} ${_billToPartyName?.address[0]?.street} ${_billToPartyName?.address[0]?.district} ${_billToPartyName?.address[0]?.city} ${_billToPartyName?.address[0]?.postCode}`;
  };

  const [
    saleGroupChoices,
    divisionChoices,
    saleOfficeChoices,
    distributionChannelChoices,
    saleOrganizationChoices
  ] = useSales(dataOrder?.salesOrg);

  const getValueFromCode = (code, choices) => {
    const option = choices.find(item => item.code === code);
    return option?.value;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      billTo: getBillToPartyName()
        ? getBillToPartyName()
        : getBillToPartyAddress()
        ? getBillToPartyAddress()
        : "",
      shipTo: getShipToPartyName()
        ? getShipToPartyName()
        : getShipToPartyAddress()
        ? getShipToPartyAddress()
        : "",
      customerGroup1: dataOrder?.customerGroup1,
      customerGroup2: dataOrder?.customerGroup2,
      customerGroup3: dataOrder?.customerGroup3,
      customerGroup4: dataOrder?.customerGroup4,
      poDate: dataOrder?.poDate
        ? moment(dataOrder?.poDate, "DD/MM/YYYY").format("YYYY-MM-DD")
        : "",
      poNo: dataOrder?.poNo,
      deliveryBlock: dataOrder?.deliveryBlock,
      incoterm: dataOrder?.incoterms1,
      internalCommentsToLogistic: getInternalCommentsToLogistic(),
      internalCommentsToWarehouse: getInternalCommentsToWarehouse(),
      externalCommentsToCustomer: getExternalCommentsToCustomer(),
      productInformation: getProductInformation(),
      saleOrganization: getValueFromCode(
        dataOrder?.salesOrg,
        saleOrganizationChoices
      ),
      distributionChannel: getValueFromCode(
        dataOrder?.distributionChannel,
        distributionChannelChoices
      ),
      division: getValueFromCode(dataOrder?.division, divisionChoices),
      saleGroup: getValueFromCode(dataOrder?.salesGroup, saleGroupChoices),
      // salesGroup: dataOrder?.salesGroup,
      saleOffice: getValueFromCode(dataOrder?.salesOff, saleOfficeChoices)
    },
    validationSchema: headerTabSchema,
    onSubmit: data => {
      if (submitKey) {
        onSubmitForm(data);
      }
    }
  });

  const { data: dataCustomerGroup } = useGetCustomerGroupMasterQuery();

  const mapCustomerGroupToChoices = array =>
    array.map(item => ({
      value: item.code,
      label: item.name
    }));

  const customerGroup1 = useMemo(
    () => mapCustomerGroupToChoices(dataCustomerGroup?.customerGroup1 || []),
    [dataCustomerGroup]
  );
  const customerGroup2 = useMemo(
    () => mapCustomerGroupToChoices(dataCustomerGroup?.customerGroup2 || []),
    [dataCustomerGroup]
  );
  const customerGroup3 = useMemo(
    () => mapCustomerGroupToChoices(dataCustomerGroup?.customerGroup3 || []),
    [dataCustomerGroup]
  );
  const customerGroup4 = useMemo(
    () => mapCustomerGroupToChoices(dataCustomerGroup?.customerGroup4 || []),
    [dataCustomerGroup]
  );

  useEffect(() => {
    if (cancelEditCall) {
      formik.resetForm();
    }
  }, [cancelEditCall]);

  useEffect(() => {
    if (submitKey) {
      formik.validateForm().then(error => {
        const errorArr = Object.keys(error);
        if (errorArr.length) {
          setOpenError(true);
          setActiveTabTotal(ChangeOrderCSTab.header);
          setSubmitKey(0);
          setActiveTab(ContentHeaderTab.partner);
        }
      });
      if (isStaff) {
        callCreditLimit().then(res => {
          if (res?.data?.getCreditLimit?.creditBlockStatus) {
            toggleCloseCreateOrderError();
            return;
          }
          formik.handleSubmit();
        });
      } else {
        formik.handleSubmit();
      }
    }
  }, [submitKey]);

  useEffect(() => {
    if (applyAllKey) {
      onApplyAll(formik.values);
      setApplyKey(0);
    }
  }, [applyAllKey]);

  const onTabClick = tab => {
    setActiveTab(tab);
  };

  useEffect(() => {
    if (!formik.values.saleOrganization && saleOrganizationChoices.length) {
      formik.setFieldValue(
        "saleOrganization",
        saleOrganizationChoices[0].value
      );
    }
  }, [saleOrganizationChoices]);

  useEffect(() => {
    if (
      !formik.values.distributionChannel &&
      distributionChannelChoices.length
    ) {
      formik.setFieldValue(
        "distributionChannel",
        distributionChannelChoices[0].value
      );
    }
  }, [distributionChannelChoices]);

  useEffect(() => {
    if (!formik.values.division && divisionChoices.length) {
      formik.setFieldValue("division", divisionChoices[0].value);
    }
  }, [divisionChoices]);

  useEffect(() => {
    if (!formik.values.saleGroup && saleGroupChoices.length) {
      formik.setFieldValue("saleGroup", saleGroupChoices[0].value);
    }
  }, [saleGroupChoices]);

  useEffect(() => {
    if (!formik.values.saleOffice && saleOfficeChoices.length) {
      formik.setFieldValue("saleOffice", saleOfficeChoices[0].value);
    }
  }, [saleOfficeChoices]);

  const [openCreateOrderError, setOpenCreateOrderError] = useState<boolean>(
    false
  );

  const isStaff = isStaffRole();

  const toggleCloseCreateOrderError = () => {
    setOpenCreateOrderError(val => !val);
  };

  const renderPartnerTab = (
    <PartnerTab
      orderId={orderId}
      dataOrder={dataOrder}
      soldToCode={getSoldToCode()}
      formik={formik}
      setBillToInsteaded={setBillToInsteaded}
      setShipToInsteaded={setShipToInsteaded}
      customerGroup1={customerGroup1}
      customerGroup2={customerGroup2}
      customerGroup3={customerGroup3}
      customerGroup4={customerGroup4}
    />
  );
  const renderAdditionalTab = (
    <AdditionalTab
      formik={formik}
      dataOrder={dataOrder}
      internalCommentsToLogistic={getInternalCommentsToLogistic()}
      internalCommentsToWarehouse={getInternalCommentsToWarehouse()}
      externalCommentsToCustomer={getExternalCommentsToCustomer()}
      productInformation={getProductInformation()}
    />
  );
  const renderSalesTab = (
    <SalesTab
      formik={formik}
      dataOrder={dataOrder}
      saleOrganizationChoices={saleOrganizationChoices}
      distributionChannelChoices={distributionChannelChoices}
      divisionChoices={divisionChoices}
      saleGroupChoices={saleGroupChoices}
      saleOfficeChoices={saleOfficeChoices}
      orderTypeDesc={dataOrder?.orderTypeDesc}
    />
  );

  return (
    <>
      <div className={classes.root}>
        <div className={classes.tabBox}>
          {renderCollection(tabList, tab => (
            <div
              key={tab.code}
              className={classNames(
                activeTab === tab.code ? classes.zMax : classes[tab.index]
              )}
            >
              <Button
                id={tab.id}
                data-testid={tab.id}
                onClick={() => onTabClick(tab.code)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    onTabClick(tab.code);
                  }
                }}
                className={classNames(
                  !tab.first && classes.tabB,
                  classes.tableTab,
                  activeTab === tab.code
                    ? classes.tableTabActive
                    : classes.tableTabInactive
                )}
              >
                <MyTypography
                  variant={"body2"}
                  className={classNames(
                    activeTab === tab.code
                      ? classes.innerTabActive
                      : classes.innerTab
                  )}
                >
                  {tab.title}
                </MyTypography>
              </Button>
            </div>
          ))}
        </div>
        <div className={classes.boxContentTab}>
          {activeTab === ContentHeaderTab.partner ? renderPartnerTab : ""}
          {activeTab === ContentHeaderTab.additionalData
            ? renderAdditionalTab
            : ""}
          {activeTab === ContentHeaderTab.sales ? renderSalesTab : ""}
        </div>
      </div>
      <DialogCreateOrderError
        onConfirm={() => {
          toggleCloseCreateOrderError();
          if (isStaff) {
            formik.handleSubmit();
          }
        }}
        open={openCreateOrderError}
        okText="ปิดหน้าต่าง"
        title={
          isStaff ? "ลูกค้ารายนี้ติด Credit Block" : "ไม่สามารถดำเนินการต่อได้"
        }
        content={
          isStaff ? (
            <MyTypography variant="body2" className={classes.inlineBlock}>
              เนื่องจากลูกค้ารายนี้ติด Credit Block กรุณาติดต่อฝ่ายสินเชื่อ
            </MyTypography>
          ) : (
            <span>
              <MyTypography variant="body2" className={classes.inlineBlock}>
                เนื่องจาก Customer Credit :
              </MyTypography>{" "}
              <MyTypography variant="subtitle2" className={classes.inlineBlock}>
                Credit Blocked
              </MyTypography>
            </span>
          )
        }
      />
    </>
  );
};

export default HeaderTab;
